import type { ManeuverState } from '../maneuvers/progress';
import type { RouteFeature } from '../types';

export type ContextKind = 'traffic' | 'rain' | 'toll' | 'works' | 'elevation' | 'difficulty' | 'fuel';

export interface RouteContext {
  maneuver: ManeuverState;
  traffic?: { travelTimeS: number; delayS?: number };
  rain?: { sampledPoints: number; echoPoints: number; maxSignal: number };
  toll?: { plazaCount: number; nearestAheadKm?: number; nextRole?: string; source: string; nextRoleConfidence: string };
  works?: { count: number; nearestAheadKm?: number };
  elevation?: {
    ascentM: number;
    source?: string;
  };
  difficulty?: {
    steepClimbKm?: number;
    hairpinCount?: number;
    source: string;
    confidence?: string;
  };
  fuel?: { stationCount: number; corridorKm: number };
}

export interface ContextCard {
  kind: ContextKind;
  title: string;
  value: string;
  source?: string;
  confidence?: string;
  relevance: number;
}

export interface FocusedManeuverProgress {
  routeKey: string;
  progressM: number;
}

interface SanitizedTrafficEnvelope {
  status?: unknown;
  tomtom?: {
    travel_time_s?: unknown;
    traffic_delay_s?: unknown;
  };
}

export function normalizeLiveTraffic(value: unknown): RouteContext['traffic'] {
  if (!value || typeof value !== 'object') return undefined;
  const envelope = value as SanitizedTrafficEnvelope;
  const travelTimeS = Number(envelope.tomtom?.travel_time_s);
  const delayS = Number(envelope.tomtom?.traffic_delay_s);
  if (envelope.status !== 'ok' || !Number.isFinite(travelTimeS) || travelTimeS <= 0) return undefined;
  return {
    travelTimeS,
    delayS: Number.isFinite(delayS) && delayS >= 0 ? delayS : undefined
  };
}

function compactDurationText(seconds: number): string {
  const totalMinutes = seconds > 0 ? Math.max(1, Math.ceil(seconds / 60)) : 0;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes.toString().padStart(2, '0')}` : `${minutes}m`;
}

export function formatLiveTrafficSummary(value: unknown): string {
  const traffic = normalizeLiveTraffic(value);
  if (!traffic) return '';
  const delay = finitePositive(traffic.delayS) ? ` (+${compactDurationText(traffic.delayS)})` : '';
  return `live ${compactDurationText(traffic.travelTimeS)}${delay}`;
}

function updateFnv1a(hash: number, value: unknown): number {
  const token = value === undefined ? '<undefined>' : String(value);
  let next = hash;
  for (let index = 0; index < token.length; index += 1) {
    next ^= token.charCodeAt(index);
    next = Math.imul(next, 0x01000193) >>> 0;
  }
  next ^= 0x1f;
  return Math.imul(next, 0x01000193) >>> 0;
}

export function fingerprintManeuverRoute(route: RouteFeature): string {
  let hash = 0x811c9dc5;
  hash = updateFnv1a(hash, route.properties.route_num);
  hash = updateFnv1a(hash, route.geometry.coordinates.length);
  for (const coordinate of route.geometry.coordinates) {
    hash = updateFnv1a(hash, coordinate.length);
    for (const value of coordinate) hash = updateFnv1a(hash, value);
  }
  const instructions = route.properties.instructions || [];
  hash = updateFnv1a(hash, instructions.length);
  for (const instruction of instructions) {
    hash = updateFnv1a(hash, instruction.sign);
    hash = updateFnv1a(hash, instruction.street_name);
    hash = updateFnv1a(hash, instruction.distance_m);
    hash = updateFnv1a(hash, instruction.time_ms);
    hash = updateFnv1a(hash, instruction.interval[0]);
    hash = updateFnv1a(hash, instruction.interval[1]);
    hash = updateFnv1a(hash, instruction.exit_number);
    hash = updateFnv1a(hash, instruction.exited);
    hash = updateFnv1a(hash, instruction.turn_angle);
    hash = updateFnv1a(hash, instruction.text);
  }
  return `${route.properties.route_num}:${hash.toString(16).padStart(8, '0')}`;
}

export function maneuverProgressForRoute(
  saved: FocusedManeuverProgress | null,
  routeKey: string
): number | null {
  return saved?.routeKey === routeKey ? saved.progressM : null;
}

export function persistManeuverProgress(
  saved: FocusedManeuverProgress | null,
  routeKey: string,
  progressM: number
): FocusedManeuverProgress {
  const prior = maneuverProgressForRoute(saved, routeKey) ?? 0;
  return { routeKey, progressM: Math.max(prior, Number.isFinite(progressM) ? progressM : 0) };
}

function finitePositive(value: number | undefined): value is number {
  return Number.isFinite(value) && (value as number) > 0;
}

function durationText(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
}

function distanceSuffix(distanceKm: number | undefined): string {
  return finitePositive(distanceKm) ? ` · next in ${distanceKm.toFixed(1)} km` : '';
}

export function rankRouteContext(context: RouteContext): ContextCard[] {
  const cards: ContextCard[] = [];

  if (context.works && context.works.count > 0) {
    cards.push({
      kind: 'works',
      title: 'Road works ahead',
      value: `${context.works.count} ${context.works.count === 1 ? 'work zone' : 'work zones'}${distanceSuffix(context.works.nearestAheadKm)}`,
      relevance: 120 - Math.min(context.works.nearestAheadKm ?? 20, 20)
    });
  }

  if (context.traffic && finitePositive(context.traffic.travelTimeS)) {
    const delay = context.traffic.delayS;
    cards.push({
      kind: 'traffic',
      title: 'Live traffic',
      value: `ETA ${durationText(context.traffic.travelTimeS)} · ${finitePositive(delay) ? `Delay ${durationText(delay)}` : 'No reported delay'}`,
      relevance: 110 + Math.min(finitePositive(delay) ? delay / 300 : 0, 6)
    });
  }

  if (
    context.rain
    && context.rain.sampledPoints > 0
    && context.rain.echoPoints > 0
    && finitePositive(context.rain.maxSignal)
  ) {
    const coverage = Math.round((context.rain.echoPoints / context.rain.sampledPoints) * 100);
    cards.push({
      kind: 'rain',
      title: 'Rain radar on route',
      value: `Radar echoes at ${coverage}% of sampled route points`,
      relevance: 100 + Math.min(coverage / 20, 5)
    });
  }

  if (context.toll && context.toll.plazaCount > 0) {
    const nextRole = context.toll.nextRole ? ` · Next: ${context.toll.nextRole}` : '';
    cards.push({
      kind: 'toll',
      title: 'Tolls ahead',
      value: `${context.toll.plazaCount} ${context.toll.plazaCount === 1 ? 'plaza' : 'plazas'}${distanceSuffix(context.toll.nearestAheadKm)}${nextRole}`,
      source: context.toll.source,
      confidence: context.toll.nextRoleConfidence ? `Next toll role: ${context.toll.nextRoleConfidence}` : undefined,
      relevance: 90 - Math.min(context.toll.nearestAheadKm ?? 20, 20)
    });
  }

  if (
    context.elevation
    && finitePositive(context.elevation.ascentM)
  ) {
    cards.push({
      kind: 'elevation',
      title: 'Route elevation',
      value: `${Math.round(context.elevation.ascentM)} m ascent`,
      source: context.elevation.source,
      confidence: undefined,
      relevance: 70
    });
  }

  if (
    context.difficulty
    && (finitePositive(context.difficulty.steepClimbKm) || finitePositive(context.difficulty.hairpinCount))
  ) {
    const details = [
      finitePositive(context.difficulty.steepClimbKm) ? `${context.difficulty.steepClimbKm.toFixed(1)} km steep climb` : '',
      finitePositive(context.difficulty.hairpinCount) ? `${context.difficulty.hairpinCount} hairpins` : ''
    ].filter(Boolean);
    cards.push({
      kind: 'difficulty',
      title: 'Route difficulty',
      value: details.join(' · '),
      source: context.difficulty.source,
      confidence: context.difficulty.confidence,
      relevance: 72 + Math.min((context.difficulty.steepClimbKm ?? 0), 5)
    });
  }

  if (context.fuel && context.fuel.stationCount > 0 && finitePositive(context.fuel.corridorKm)) {
    cards.push({
      kind: 'fuel',
      title: 'Fuel near route',
      value: `${context.fuel.stationCount} ${context.fuel.stationCount === 1 ? 'station' : 'stations'} within ${context.fuel.corridorKm} km`,
      relevance: 60
    });
  }

  return cards.sort((left, right) => right.relevance - left.relevance).slice(0, 3);
}
